/*
* Project: BookingEngine
* Module: utils/loyaltyEmailStatus.js
*
* Lógica compartida del pre-check de email de loyalty (checkRegistrationEmail)
* para los formularios de registro (UserRegistration2), login (Login2) y
* forgot password (RecoverPassword).
*/

export const EMAIL_STATUS = Object.freeze({
  NOT_FOUND: 'NOT_FOUND',
  INVALID_EMAIL: 'INVALID_EMAIL',
  PENDING_VERIFICATION: 'PENDING_VERIFICATION',
  UNSUBSCRIBED: 'UNSUBSCRIBED',
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
});

export const EMAIL_FORM = Object.freeze({
  REGISTER: 'register',
  LOGIN: 'login',
  FORGOT: 'forgot',
});

export const EMAIL_CHECK_DEBOUNCE_MS = 400;

export const LOYALTY_SUPPORT_EMAIL = 'sandos4u.mx@sandos.com';

// Error que regresa el backend cuando se pide otro reenvío demasiado pronto
export const RESEND_ERROR_THROTTLED = 'THROTTLED';

const EMAIL_FORMAT_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Status que impiden enviar cada formulario
const SUBMIT_BLOCKING_STATUSES = Object.freeze({
  [EMAIL_FORM.REGISTER]: [
    EMAIL_STATUS.INVALID_EMAIL,
    EMAIL_STATUS.PENDING_VERIFICATION,
    EMAIL_STATUS.UNSUBSCRIBED,
    EMAIL_STATUS.ACTIVE,
    EMAIL_STATUS.BLOCKED,
  ],
  [EMAIL_FORM.LOGIN]: [
    EMAIL_STATUS.PENDING_VERIFICATION,
    EMAIL_STATUS.UNSUBSCRIBED,
    EMAIL_STATUS.BLOCKED,
  ],
  [EMAIL_FORM.FORGOT]: [
    EMAIL_STATUS.PENDING_VERIFICATION,
    EMAIL_STATUS.UNSUBSCRIBED,
    EMAIL_STATUS.BLOCKED,
    EMAIL_STATUS.NOT_FOUND,
  ],
});

export const normalizeEmail = (email) => (email || '').trim().toLowerCase();

export const isEmailFormatValid = (email) => EMAIL_FORMAT_REGEX.test((email || '').trim());

export const isSubmitBlockedByEmailStatus = (form, status) => (
  Boolean(status) && (SUBMIT_BLOCKING_STATUSES[form] || []).indexOf(status) !== -1
);

export const canResendActivationLink = (status) => (
  status === EMAIL_STATUS.PENDING_VERIFICATION || status === EMAIL_STATUS.UNSUBSCRIBED
);

// Estado inicial que cada formulario mezcla en su state
export const getInitialEmailCheckState = () => ({
  emailStatus: null,
  emailCheckLoading: false,
  emailCheckFailed: false,
  // Email (normalizado) al que corresponde emailStatus
  emailCheckedValue: null,
  isResending: false,
  resendError: '',
  showLinkSentModal: false,
});

// true si ya tenemos un resultado definitivo del check para ese email
export const isEmailStatusSettled = (state, email) => (
  !state.emailCheckLoading && state.emailCheckedValue !== null
  && state.emailCheckedValue === normalizeEmail(email)
);

/*
* Algunos mensajes de loyalty al fallar el login indican que la cuenta aún no
* está activada (type "accountNotActivated" / "Account not yet activated.").
* En ese caso se trata igual que PENDING_VERIFICATION.
*/
export const isAccountNotActivatedMessage = (message) => {
  if (!message || typeof message !== 'string') {
    return false;
  }
  const normalized = message.toLowerCase();
  return normalized.indexOf('accountnotactivated') !== -1 || normalized.indexOf('not yet activated') !== -1;
};

/*
* Ejecuta el check de email con debounce y descarta respuestas viejas
* (si el usuario sigue tecleando, sólo cuenta la respuesta del último request).
*
* - check(email): función que regresa una Promise que resuelve con el status
*   o se rechaza si el check no pudo completarse.
* - onUpdate(partialState): recibe el estado a mezclar en el componente.
*/
export class EmailStatusChecker {
  constructor({ check, onUpdate, delay = EMAIL_CHECK_DEBOUNCE_MS }) {
    this.check = check;
    this.onUpdate = onUpdate;
    this.delay = delay;
    this.timer = null;
    this.requestId = 0;
    this.disposed = false;
  }

  // Se llama en cada cambio del input
  schedule(rawEmail) {
    const email = normalizeEmail(rawEmail);
    this.cancel();
    // Se limpia el status de inmediato para no mostrar el resultado de un email anterior
    this.emit(EmailStatusChecker.idleState());
    if (!isEmailFormatValid(email)) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      this.run(email);
    }, this.delay);
  }

  /*
  * Ejecuta el check sin debounce (p. ej. al enviar el formulario).
  * Resuelve con el status, con null si falló, o con undefined si la
  * respuesta quedó obsoleta porque el usuario cambió el email.
  */
  checkNow(rawEmail) {
    const email = normalizeEmail(rawEmail);
    this.cancel();
    if (!isEmailFormatValid(email)) {
      this.emit(EmailStatusChecker.idleState());
      return Promise.resolve(null);
    }
    return this.run(email);
  }

  run(email) {
    this.requestId += 1;
    const { requestId } = this;
    this.emit({
      emailStatus: null, emailCheckLoading: true, emailCheckFailed: false, emailCheckedValue: null,
    });

    let request;
    try {
      request = Promise.resolve(this.check(email));
    } catch (error) {
      request = Promise.reject(error);
    }

    return request.then(
      (status) => {
        if (!this.isCurrent(requestId)) {
          return undefined;
        }
        const emailStatus = status || null;
        this.emit({
          emailStatus, emailCheckLoading: false, emailCheckFailed: false, emailCheckedValue: email,
        });
        return emailStatus;
      },
      () => {
        if (!this.isCurrent(requestId)) {
          return undefined;
        }
        // Un error técnico nunca bloquea el formulario
        this.emit({
          emailStatus: null, emailCheckLoading: false, emailCheckFailed: true, emailCheckedValue: email,
        });
        return null;
      },
    );
  }

  isCurrent(requestId) {
    return !this.disposed && requestId === this.requestId;
  }

  // Cancela el debounce pendiente e invalida cualquier request en vuelo
  cancel() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.requestId += 1;
  }

  // Llamar en componentWillUnmount
  dispose() {
    this.cancel();
    this.disposed = true;
  }

  emit(update) {
    if (!this.disposed && this.onUpdate) {
      this.onUpdate(update);
    }
  }

  static idleState() {
    return {
      emailStatus: null, emailCheckLoading: false, emailCheckFailed: false, emailCheckedValue: null,
    };
  }
}

/*
* Registro con email ACTIVE -> "Click to receive password reset instructions":
* el popup de forgot password debe abrir con ese email ya escrito.
* Es un valor de un solo uso: RecoverPassword lo toma al montarse.
*/
let forgotPasswordPrefillEmail = null;

export const setForgotPasswordPrefillEmail = (email) => {
  forgotPasswordPrefillEmail = (email || '').trim() || null;
};

export const takeForgotPasswordPrefillEmail = () => {
  const email = forgotPasswordPrefillEmail;
  forgotPasswordPrefillEmail = null;
  return email;
};
